/**
 * The ceremony's sound — and the switch that keeps it quiet.
 *
 * The cues sit at or under YOU WON, and Adam has now approved them from the
 * clips — so the ceremony's sound is ON, as it is in Blockari. This is still
 * the only place that decides, and it can be turned off by hand:
 *
 *   ?ceremonyAudio=0                 — this page load
 *   localStorage w21.ceremonyAudio=0 — this browser, until cleared
 *
 * Turning it on for everybody is one line here, once the sound has been signed
 * off, and nothing else has to change.
 *
 * The cues themselves are Blockari's, vendored — see `audio/ceremony-kit.ts`.
 */

import { audio } from '@/audio';
import { CHESTS_ENABLED } from '@/core/flags';
import type { ChestBeat } from '@/audio/ceremony-kit';

const KEY = 'w21.ceremonyAudio';

let asked: boolean | null = null;

/**
 * ON by default, as of Adam's approval of the clips — the same as Blockari.
 * `?ceremonyAudio=0` still turns it off for a page load, and the sheet's own
 * Sound switch mutes everything as it always did.
 */
export function ceremonyAudioEnabled(): boolean {
  if (asked !== null) return asked;
  asked = true;
  if (typeof window === 'undefined') return asked;
  try {
    const flag = new URL(window.location.href).searchParams.get('ceremonyAudio');
    if (flag === '0' || flag === 'off') asked = false;
    else if (flag === '1' || flag === 'on') asked = true;
    else asked = window.localStorage?.getItem(KEY) !== '0';
  } catch {
    // An odd URL or blocked storage leaves it on, which is now the default.
    asked = true;
  }
  return asked;
}

/** Tooling and, one day, the settings sheet. */
export function setCeremonyAudio(on: boolean): void {
  asked = on;
  try {
    window.localStorage?.setItem(KEY, on ? '1' : '0');
  } catch {
    // Not being able to remember it is not a reason to refuse it now.
  }
}

export function playRankUp(rankIndex: number): void {
  if (!ceremonyAudioEnabled()) return;
  audio.rankUp(rankIndex);
}

export function playLevelFlip(): void {
  if (!ceremonyAudioEnabled()) return;
  audio.levelFlip();
}

/**
 * A chest beat. `step` is the rung on a climb and the card's index on a deal,
 * so the climb and the deal both rise.
 */
export function playChestBeat(beat: ChestBeat, step = 0): void {
  // The chests are not live yet, so neither are their cues — the ceremony's
  // own sound being on says nothing about a feature that is off.
  if (!CHESTS_ENABLED || !ceremonyAudioEnabled()) return;
  audio.chestBeat(beat, step);
}

/**
 * A coin of a payout landing in the balance: Blockari's `clear-1`, low, its rate climbing coin by coin.
 * Behind the same switch as the rest of the ceremony's sound (`?ceremonyAudio=0`), and not tied to the
 * chests: a win pays whether or not they are live.
 */
export function playCoinLand(step: number, total: number): void {
  if (!ceremonyAudioEnabled()) return;
  audio.coinLand(step, total);
}
